/**
 * Amazon Australia domain foundation (PHASE1-AMAZON-AU-DATA-READINESS-020C).
 *
 * Authoritative product requirements come from Amazon Product Type Definitions
 * v2020-09-01 for marketplace A39IBJ37TRP1C6. This module is the type/constant
 * boundary for future PTD fetch (020D). It never calls Amazon and never treats
 * guessed brand/GTIN/images as listing truth.
 */

import type { MarketplaceReadinessStatus, ProviderReadiness } from "@/lib/marketplace-readiness";
import {
  evaluateAmazonMerchantShippingReadiness,
  parseAmazonMerchantShippingFromPtd,
  type AmazonListingShippingConfig,
} from "./amazon-merchant-shipping";
import type { ShippingDimensions } from "@/lib/shipping-facts";

export const AMAZON_PROVIDER = "amazon" as const;

export const AMAZON_AU_MARKETPLACE_ID = "A39IBJ37TRP1C6" as const;
export const AMAZON_AU_COUNTRY_CODE = "AU" as const;
export const AMAZON_SP_API_REGION = "FE" as const;
export const AMAZON_SP_API_ENDPOINT = "https://sellingpartnerapi-fe.amazon.com" as const;
export const AMAZON_AU_LOCALE = "en_AU" as const;
export const AMAZON_PTD_API_VERSION = "2020-09-01" as const;

export const AMAZON_AU_MARKETPLACE = {
  provider: AMAZON_PROVIDER,
  marketplaceId: AMAZON_AU_MARKETPLACE_ID,
  countryCode: AMAZON_AU_COUNTRY_CODE,
  region: AMAZON_SP_API_REGION,
  endpoint: AMAZON_SP_API_ENDPOINT,
  locale: AMAZON_AU_LOCALE,
  ptdApiVersion: AMAZON_PTD_API_VERSION,
} as const;

export type AmazonMarketplaceContext = {
  marketplaceId: string;
  countryCode: string;
  region: string;
  locale: string;
};

export type AmazonProductTypeSelection = {
  productType: string;
  displayName: string;
  marketplaceId: string;
  operatorConfirmed: boolean;
};

export type AmazonRequirementsSet = "LISTING" | "LISTING_PRODUCT_ONLY" | "LISTING_OFFER_ONLY";
export type AmazonRequirementsEnforced = "ENFORCED" | "NOT_ENFORCED";
export type AmazonParentageLevel = "NONE" | "CHILD" | "PARENT";

export type AmazonSchemaFreshness = "fresh" | "stale" | "unknown" | "error";

export type AmazonSchemaIdentity = {
  productType: string;
  marketplaceId: string;
  requirements: AmazonRequirementsSet;
  requirementsEnforced: AmazonRequirementsEnforced;
  parentageLevel: AmazonParentageLevel;
  locale: string;
  productTypeVersion: string | null;
  schemaChecksum: string | null;
  fetchedAt: string | null;
  freshness: AmazonSchemaFreshness;
};

export type AmazonRequirementLevel = "required" | "conditionally_required" | "optional";
export type AmazonVariationRole = "none" | "variation_theme" | "variation_dimension" | "relationship";

export type AmazonRequirement = {
  attributeKey: string;
  displayName: string;
  requirementLevel: AmazonRequirementLevel;
  dataType: string | null;
  allowedValues: { value: string; displayName: string }[];
  unit: string | null;
  repeatable: boolean;
  parentApplicable: boolean;
  childApplicable: boolean;
  standaloneApplicable: boolean;
  variationRole: AmazonVariationRole;
  propertyGroup: string | null;
  hidden: boolean;
  editable: boolean;
  lifecycle: { enumDeprecated?: string[] } | null;
  conditionalExpression: string | null;
};

/** 020D will implement this. 020C only defines the cache key / freshness contract. */
export type AmazonPtdCacheKey = {
  marketplaceId: string;
  productType: string;
  requirements: AmazonRequirementsSet;
  requirementsEnforced: AmazonRequirementsEnforced;
  parentageLevel: AmazonParentageLevel;
  locale: string;
  productTypeVersion: string | null;
  schemaChecksum: string | null;
  sellerId?: string | null;
};

export type AmazonPtdSourceRecord = {
  key: AmazonPtdCacheKey;
  definition: unknown | null;
  schema: unknown | null;
  fetchedAt: string | null;
  freshness: AmazonSchemaFreshness;
  error: string | null;
};

export type AmazonPtdEvaluation = {
  authoritative: boolean;
  missingRequiredAttributeKeys: string[];
  needsApproval: boolean;
  error: string | null;
};

export function isAmazonProvider(provider: string | null | undefined): boolean {
  return (provider ?? "").trim().toLowerCase() === AMAZON_PROVIDER;
}

export function amazonAuMarketplaceContext(): AmazonMarketplaceContext {
  return {
    marketplaceId: AMAZON_AU_MARKETPLACE_ID,
    countryCode: AMAZON_AU_COUNTRY_CODE,
    region: AMAZON_SP_API_REGION,
    locale: AMAZON_AU_LOCALE,
  };
}

export function amazonPtdCacheKey(identity: AmazonSchemaIdentity, sellerId?: string | null): AmazonPtdCacheKey {
  return {
    marketplaceId: identity.marketplaceId,
    productType: identity.productType,
    requirements: identity.requirements,
    requirementsEnforced: identity.requirementsEnforced,
    parentageLevel: identity.parentageLevel,
    locale: identity.locale,
    productTypeVersion: identity.productTypeVersion,
    schemaChecksum: identity.schemaChecksum,
    sellerId: sellerId ?? null,
  };
}

export function amazonSchemaIsAuthoritative(schema: AmazonSchemaIdentity | null | undefined): boolean {
  if (!schema) return false;
  if (schema.marketplaceId !== AMAZON_AU_MARKETPLACE_ID) return false;
  if (!schema.productType.trim()) return false;
  if (!schema.productTypeVersion?.trim()) return false;
  if (!schema.schemaChecksum?.trim()) return false;
  if (schema.freshness !== "fresh") return false;
  if (!schema.fetchedAt?.trim()) return false;
  return true;
}

function amazonRow(
  status: MarketplaceReadinessStatus,
  summary: string,
  missingFields: ProviderReadiness["missingFields"] = [],
): ProviderReadiness {
  return {
    provider: AMAZON_PROVIDER,
    displayName: "Amazon",
    status,
    missingFields,
    summary,
  };
}

/**
 * Fail-closed Amazon AU readiness. READY is only possible when a fresh PTD
 * schema has been evaluated authoritatively. 020C never fetches PTD, so
 * production calls without an injected evaluation cannot return READY.
 */
export function evaluateAmazonAuReadiness(opts: {
  connected: boolean;
  productTypeSelection?: AmazonProductTypeSelection | null;
  schema?: AmazonSchemaIdentity | null;
  evaluation?: AmazonPtdEvaluation | null;
  providerError?: string | null;
  needsApproval?: boolean;
  /** Seller-specific PTD JSON. Generic schemas cannot make shipping READY. */
  sellerSpecific?: boolean;
  ptdSchema?: unknown;
  listingShipping?: AmazonListingShippingConfig | null;
  packageFacts?: ShippingDimensions | null;
}): ProviderReadiness {
  if (opts.providerError) {
    return amazonRow("PROVIDER_ERROR", opts.providerError);
  }
  if (!opts.connected) {
    return amazonRow("CHANNEL_NOT_CONNECTED", "Not connected");
  }

  const selection = opts.productTypeSelection;
  const confirmed =
    Boolean(selection?.productType?.trim()) &&
    selection?.operatorConfirmed === true &&
    selection.marketplaceId === AMAZON_AU_MARKETPLACE_ID;
  if (!confirmed) {
    return amazonRow("NEEDS_MAPPING", "Amazon product type is not confirmed");
  }

  if (opts.needsApproval || opts.evaluation?.needsApproval) {
    return amazonRow("NEEDS_APPROVAL", "Needs approval");
  }

  if (opts.evaluation?.error) {
    return amazonRow("PROVIDER_ERROR", opts.evaluation.error);
  }

  if (!amazonSchemaIsAuthoritative(opts.schema ?? null)) {
    const freshness = opts.schema?.freshness;
    if (freshness === "error") {
      return amazonRow("PROVIDER_ERROR", "Amazon Product Type Definition cache is in error");
    }
    if (freshness === "stale") {
      return amazonRow("NOT_EVALUATED", "Amazon Product Type Definition is stale");
    }
    if (opts.schema && (!opts.schema.schemaChecksum || !opts.schema.productTypeVersion)) {
      return amazonRow("NOT_EVALUATED", "Amazon Product Type Definition version/checksum is unknown");
    }
    return amazonRow("NOT_EVALUATED", "Amazon Product Type Definition has not been evaluated");
  }

  if (!opts.evaluation?.authoritative) {
    return amazonRow("NOT_EVALUATED", "Amazon Product Type Definition has not been evaluated");
  }

  if (opts.evaluation.missingRequiredAttributeKeys.length > 0) {
    return amazonRow(
      "MISSING_REQUIRED_DATA",
      `Missing ${opts.evaluation.missingRequiredAttributeKeys.length} field${
        opts.evaluation.missingRequiredAttributeKeys.length === 1 ? "" : "s"
      }`,
      opts.evaluation.missingRequiredAttributeKeys.map((key) => ({ field: "name", label: key })),
    );
  }

  if (opts.ptdSchema === undefined || opts.listingShipping == null) {
    return amazonRow("NOT_EVALUATED", "Amazon seller-specific shipping policy has not been evaluated");
  }

  const parse = parseAmazonMerchantShippingFromPtd(opts.ptdSchema, {
    productTypeVersion: opts.schema?.productTypeVersion,
    schemaChecksum: opts.schema?.schemaChecksum,
  });

  return evaluateAmazonMerchantShippingReadiness({
    sellerSpecific: opts.sellerSpecific === true,
    schema: opts.schema,
    schemaAuthoritative: true,
    parse,
    listing: opts.listingShipping,
    packageFacts: opts.packageFacts,
  });
}
